import React, { Fragment } from 'react';
import styles from './ContactFormView.module.scss';
import FooterButtons from '../FooterButtons/FooterButtons';
import { ContactForm } from './ContactForm/ContactForm';

import {
  ReportValue,
  FieldValueType,
  SectionValueType,
  UseOfForceValueType,
  ItemValueType,
  LocationAreaType,
  ReportTag
} from '../../../../Models';
import { SFScrollable } from 'sfui';
import { UseOfForceForm } from './UseOfForceForm/UseOfForceForm';
import { DurationForm } from './DurationForm/DurationForm';
import { ItemListForm } from './ItemListForm/ItemListForm';
import { Divider } from '../../../../Components/Divider/Divider';
import { SwitchField } from '../FieldInput/SwitchField/SwitchField';
import { MissingInformationModal } from './MissingInformationModal/MissingInformationModal';
import { TourTooltip } from 'ui-smartforce-settings';
import { TagsForm } from './TagsForm/TagsForm';
import { useContactFormView } from './Hooks/useContactFormView';
import { NeedHelpProvider } from './Contexts/NeedHelpContext';

const AMEND_TOOLTIP_MSG = `In the "Amend" section, you can make any changes you need to the report. Please note that you must modify at least one field to enable the "Submit" button.`;

export const CitizensContext = React.createContext<ItemValueType[]>([]);

export const ItemMissingInformationContext =
  React.createContext<boolean>(false);

/**
 * NOTE:
 * isDraft prop: Indicate that the report prop is a Draft report instead of a created report to be amended.
 */
export interface ContactFormViewProps {
  caseNumber?: string;
  generalFormAreas?: LocationAreaType[];
  report?: ReportValue;
  isDraft?: boolean;
  deviceDuration: number;
  showSupervision?: boolean;
  onCancel: () => void;
  onSubmit: (contactValue: ReportValue, isTour?: boolean) => void;
  onCleanUp?: (contactValue: ReportValue) => void;
}

export const ContactFormView = ({
  caseNumber,
  report,
  isDraft = false,
  generalFormAreas,
  deviceDuration,
  showSupervision = false,
  onCancel,
  onSubmit,
  onCleanUp
}: ContactFormViewProps): React.ReactElement<ContactFormViewProps> => {
  const {
    showStateMandated,
    isAmend,
    isMissingInfoModalOpen,
    isSubmitDisabled,
    citizens,
    officers,
    complaints,
    investigations,
    administrative,
    generalForm,
    useOfForceForm,
    agencyTags,
    validators,
    stateConfig,
    tour,
    getMissingInfoMessages,
    getCitizensMessage,
    getOfficersMessage,
    onCitizensChange,
    onContactFieldChange,
    onUseOfForceChange,
    onOfficersChange,
    onComplaintsChange,
    onInvestigationsChange,
    onAdministrativeChange,
    onTagsChange,
    onClickSubmit,
    onMissingInformationModalClose
  } = useContactFormView({
    caseNumber,
    report,
    isDraft,
    generalFormAreas,
    onSubmit,
    onCleanUp
  });

  return (
    <div className={`${styles.contactFormView} ${styles.withFooterButtons}`}>
      <MissingInformationModal
        isOpen={isMissingInfoModalOpen}
        messages={getMissingInfoMessages()}
        onClose={onMissingInformationModalClose}
      />

      <SFScrollable
        className={styles.scrollable}
        containerClassName={styles.scrollableContainer}
      >
        <CitizensContext.Provider value={citizens}>
          <div className={styles.content}>
            {!isAmend && (
              <h2 className={styles.title}>Complete Contact form</h2>
            )}

            {isAmend && tour?.id === 6 && (
              <TourTooltip
                title="“Amend” section"
                description={AMEND_TOOLTIP_MSG}
                step={2}
                lastStep={3}
                tourId={6}
                placement="bottom-start"
                preventOverflow
              >
                <h2 className={styles.title}>Amend Contact form</h2>
              </TourTooltip>
            )}

            {isAmend && tour?.id === 10 && (
              <TourTooltip
                title="“Amend” section"
                description={AMEND_TOOLTIP_MSG}
                step={2}
                lastStep={3}
                tourId={10}
                placement="bottom-start"
                preventOverflow
              >
                <h2 className={styles.title}>Amend Contact form</h2>
              </TourTooltip>
            )}

            <NeedHelpProvider>
              <div className={styles.form}>
                <div>
                  <ItemListForm
                    name="citizen"
                    className={styles.citizenSection}
                    config={stateConfig.citizen}
                    itemLabel="Citizen"
                    message={getCitizensMessage()}
                    itemListValue={citizens}
                    showStateMandated={showStateMandated}
                    onChange={onCitizensChange}
                  />

                  <ContactForm
                    value={generalForm}
                    onDefaultFieldChange={(
                      name: string,
                      value: FieldValueType
                    ) => onContactFieldChange(name, value, false)}
                    onSectionChange={(name: string, value: SectionValueType) =>
                      onContactFieldChange(name, value, true)
                    }
                  />
                </div>

                <Divider />

                <div className={styles.useOfForce}>
                  <div className={styles.useOfForceSwitch}>
                    <h3>Use of Force</h3>

                    <SwitchField
                      label="Was Use of Force Applied?"
                      value={generalForm.was_use_of_force as boolean}
                      disabled={
                        validators.uof.isActive &&
                        validators.uof.areCitizensWithUOF
                      }
                      onChange={(value: FieldValueType) =>
                        onContactFieldChange('was_use_of_force', value, false)
                      }
                    />
                  </div>

                  {stateConfig.use_of_force && generalForm.was_use_of_force && (
                    <UseOfForceForm
                      config={stateConfig.use_of_force}
                      value={useOfForceForm as UseOfForceValueType}
                      onChange={onUseOfForceChange}
                    />
                  )}
                </div>

                <Divider />

                <ItemListForm
                  name="officer"
                  config={stateConfig.officer}
                  itemLabel="Officer"
                  itemListValue={officers}
                  onChange={onOfficersChange}
                  disabled={citizens.length === 0}
                  message={getOfficersMessage()}
                  showStateMandated={showStateMandated}
                />

                <Divider />

                {/* Tags Form is only shown when not amending */}
                {!isAmend && (
                  <TagsForm
                    value={agencyTags as ReportTag[]}
                    onChange={onTagsChange}
                  />
                )}

                <Divider />

                <DurationForm
                  value={generalForm.duration as string}
                  duration={deviceDuration}
                  onChange={(value: string) =>
                    onContactFieldChange('duration', value, false)
                  }
                />

                {showSupervision && (
                  <Fragment>
                    <Divider />

                    <ItemListForm
                      name="complaint"
                      config={stateConfig.complaint}
                      itemLabel="Complaint"
                      itemListValue={complaints}
                      onChange={onComplaintsChange}
                      showStateMandated={showStateMandated}
                    />

                    <ItemListForm
                      name="investigation"
                      config={stateConfig.investigation}
                      itemLabel="Investigation"
                      itemListValue={investigations}
                      onChange={onInvestigationsChange}
                      showStateMandated={showStateMandated}
                    />
                    {stateConfig.administrative && (
                      <ItemListForm
                        name="administrative"
                        config={stateConfig.administrative}
                        itemLabel="Follow-up"
                        itemListValue={administrative}
                        onChange={onAdministrativeChange}
                        showStateMandated={showStateMandated}
                      />
                    )}
                  </Fragment>
                )}
              </div>
            </NeedHelpProvider>
          </div>
        </CitizensContext.Provider>
      </SFScrollable>

      <footer>
        <FooterButtons
          isAmend={isAmend}
          onCancel={onCancel}
          buttonAction={{
            label: 'Submit',
            disabled: isSubmitDisabled,
            onClick: onClickSubmit
          }}
        />
      </footer>
    </div>
  );
};
